import { useCallback, useEffect, useState } from 'react'
import { toast } from '../../lib/toast'
import {
  SCOPE_OPTIONS,
  createApiKey,
  listApiKeys,
  relativeTime,
  revokeApiKey,
  scopeLabel,
  type ApiKeyRow,
  type ApiScope,
  type CreatedKey,
} from './apiKeys'

/**
 * Settings → API keys. Lists the active business's keys, creates a key (shown
 * once, with a copy button) and revokes keys. See docs/api.md.
 */
export default function ApiKeysIsland() {
  const [rows, setRows] = useState<ApiKeyRow[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<ApiScope[]>(['read', 'write'])
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<CreatedKey | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)
  const [showRevoked, setShowRevoked] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setRows(await listApiKeys())
      setLoadError(null)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load API keys')
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  function toggleScope(scope: ApiScope) {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]))
  }

  async function handleCreate() {
    const trimmed = name.trim()
    if (!trimmed) {
      toast('Give the key a name', 'error')
      return
    }
    if (scopes.length === 0) {
      toast('Pick at least one scope', 'error')
      return
    }
    setCreating(true)
    try {
      const key = await createApiKey(trimmed, scopes)
      setCreated(key)
      setCopied(false)
      setName('')
      await refresh()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not create the key', 'error')
    } finally {
      setCreating(false)
    }
  }

  async function handleCopy() {
    if (!created) return
    try {
      await navigator.clipboard.writeText(created.key)
      setCopied(true)
      toast('Key copied', 'success')
    } catch {
      toast('Could not copy. Select the key and copy it by hand.', 'error')
    }
  }

  async function handleRevoke(id: string) {
    try {
      const done = await revokeApiKey(id)
      toast(done ? 'Key revoked' : 'Key was already revoked', done ? 'success' : '')
      setConfirmRevoke(null)
      await refresh()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not revoke the key', 'error')
    }
  }

  const active = (rows ?? []).filter((r) => !r.revoked_at)
  const revoked = (rows ?? []).filter((r) => r.revoked_at)

  return (
    <div data-testid="api-keys">
      {created && (
        <div
          role="alert"
          data-testid="api-key-created"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: 12,
            marginBottom: 14,
            background: 'var(--bg3)',
          }}
        >
          <div className="settings-label">Copy your key now: {created.name}</div>
          <div className="settings-hint">
            This is the only time it is shown. Hunts stores a hash, so a lost key cannot be
            recovered. Create a new one and revoke this one if you lose it.
          </div>
          <input
            className="settings-input"
            readOnly
            value={created.key}
            aria-label="New API key"
            onFocus={(e) => e.currentTarget.select()}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 12, marginTop: 8 }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn-save" onClick={() => void handleCopy()}>
              {copied ? 'Copied' : 'Copy key'}
            </button>
            <button type="button" className="sec-btn" onClick={() => setCreated(null)}>
              I have saved it
            </button>
          </div>
        </div>
      )}

      <div className="settings-row">
        <div className="settings-label">New key</div>
        <div className="settings-hint">
          One key per routine or website, so you can revoke one without touching the others. Keys
          act on the business that is open now.
        </div>
        <div
          style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <input
            className="settings-input"
            placeholder="Key name, for example daily-sender"
            aria-label="Key name"
            maxLength={60}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate()
            }}
            style={{ maxWidth: 280 }}
          />
          <button
            type="button"
            className="btn-save"
            onClick={() => void handleCreate()}
            disabled={creating}
          >
            {creating ? 'Creating…' : 'Create key'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
          {SCOPE_OPTIONS.map((o) => (
            <label
              key={o.value}
              title={o.hint}
              style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}
            >
              <input
                type="checkbox"
                checked={scopes.includes(o.value)}
                onChange={() => toggleScope(o.value)}
                aria-label={o.label}
              />
              {o.label}
            </label>
          ))}
        </div>
      </div>

      <div className="settings-row" style={{ marginBottom: 0 }}>
        <div className="settings-label">Keys</div>
        {loadError && (
          <div className="settings-hint" role="alert" style={{ color: 'var(--red)' }}>
            {loadError}
          </div>
        )}
        {rows === null && !loadError && <div className="settings-hint">Loading…</div>}
        {rows !== null && active.length === 0 && (
          <div className="settings-hint">No active keys. Create one above.</div>
        )}
        {active.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 6 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={{ padding: '4px 8px 4px 0' }}>Name</th>
                <th>Key</th>
                <th>Scopes</th>
                <th>Last used</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {active.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px 6px 0' }}>{r.name}</td>
                  <td style={{ fontFamily: 'var(--font-mono)' }}>hk_{r.prefix}_…</td>
                  <td>{scopeLabel(r.scopes)}</td>
                  <td>{relativeTime(r.last_used_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {confirmRevoke === r.id ? (
                      <>
                        <button
                          type="button"
                          className="sec-btn"
                          onClick={() => void handleRevoke(r.id)}
                        >
                          Confirm revoke
                        </button>{' '}
                        <button
                          type="button"
                          className="sec-btn"
                          onClick={() => setConfirmRevoke(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="sec-btn"
                        onClick={() => setConfirmRevoke(r.id)}
                        aria-label={`Revoke ${r.name}`}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {revoked.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button type="button" className="sec-btn" onClick={() => setShowRevoked((v) => !v)}>
              {showRevoked ? 'Hide' : 'Show'} {revoked.length} revoked
            </button>
            {showRevoked && (
              <ul className="settings-hint" style={{ marginTop: 6 }}>
                {revoked.map((r) => (
                  <li key={r.id}>
                    {r.name} (hk_{r.prefix}_…) revoked {relativeTime(r.revoked_at)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
